import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Pharmacogenomics series — Codeine/Opioid Safety, CYP2D6 ultra-rapid metabolizer.

export const opioidBrigid: Scenario = {
  slug: 'opioid-brigid',
  series: 'Pharmacogenomics',
  title: "One codeine tablet hit like far more",
  socialHook:
    "Brigid, 41, had given codeine to patients for years. Then one standard post-surgery dose left her dizzy, confused, and unable to stand. Her genetics explained why the same tablet can behave very differently.",
  meta: {
    description:
      "A fictional case study: Brigid is a nurse prescribed codeine 30mg after minor surgery. Within an hour she had extreme nausea, dizziness, drowsiness, and confusion. Her CYP2D6 result showed that her body turns codeine into morphine unusually fast, changing the safety of future prescriptions.",
  },

  persona: {
    name: 'Brigid',
    age: 41,
    sex: 'Female',
    ancestry: 'Irish',
    occupation: 'Nurse',
    chiefConcern:
      "Six weeks ago, Brigid took codeine 30mg after minor surgery. Within an hour she was extremely nauseous, dizzy, too drowsy to stand, and briefly confused. It felt as if she had taken far more than prescribed. She was told some people are just sensitive to opioids, but that answer did not fit what she knows as a nurse. She wants to know what happened and what pain medicines are safe for her in the future.",
    familyHistory: 'Younger sister had a strikingly similar reaction to codeine following post-operative analgesia.',
  },

  clinicalPicture: {
    symptoms: [
      'Extreme nausea within one hour of first codeine dose',
      'Severe dizziness — unable to maintain balance',
      'Profound drowsiness — could not stand or move safely',
      'Brief episode of confusion lasting around 20 minutes',
    ],
    labs: [
      {
        name: 'CYP2D6 phenotype (retrospective pharmacogenetic test)',
        value: 'Ultra-rapid metabolizer confirmed (gene duplication detected)',
        refRange: 'Normal metabolizer (no duplication)',
        flag: 'high',
      },
    ],
    medications: ['Codeine 30mg (single dose, post-operative — not continued after the incident)'],
    supplements: ['No regular supplements'],
    lifestyle: [
      'Active working nurse — physically demanding role',
      'Non-smoker',
      'Occasional alcohol socially',
      'No regular opioid use prior to this incident — opioid-naive at the time of the dose',
    ],
  },

  genetics: [
    {
      gene: 'CYP2D6',
      variant: 'Gene duplication (ultra-rapid metabolizer)',
      genotype: 'Ultra-rapid metabolizer (CYP2D6 UM)',
      plainMeaning:
        "Brigid's body converts codeine into morphine much faster and more completely than normal — so a standard tablet delivered the effect of a much larger dose.",
      technicalDetail:
        "CYP2D6 is the liver enzyme that converts codeine (a prodrug) into its active form, morphine. In a normal metabolizer, roughly 10% of a codeine dose is converted; standard dosing is calibrated for that rate. Brigid carries a duplication of the CYP2D6 gene, giving her more functional enzyme than normal. This dramatically accelerates and increases conversion, producing far more morphine equivalents from a standard dose in a shorter timeframe — in an opioid-naive patient, the effect is equivalent to receiving an acute morphine overdose. Codeine and tramadol are contraindicated in ultra-rapid CYP2D6 metabolizers. This pharmacogenetic risk contributed to the EU withdrawal of codeine from children under 12 after fatalities in ultra-rapid metabolizer children.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "The familiar tablet became the clue",
      body: "Brigid is a nurse. She has given codeine to patients hundreds, probably thousands, of times. She knows what it is for and what side effects to watch for. When she was prescribed it after a minor procedure, she did not worry. She took it at home, alone, and within an hour she could not stand. The nausea was severe. The drowsiness felt heavy and unsafe. For about 20 minutes, her thoughts would not line up. She knew this was not ordinary sensitivity. She had seen too many patients take codeine without this reaction.",
    },
    {
      type: 'narrative',
      heading: "Codeine only works after the body changes it",
      body: "The missing piece is that codeine is not the active pain reliever. The liver must convert it into morphine first. Standard codeine dosing assumes that conversion happens at a usual speed, creating a modest amount of morphine over time. Brigid's body does not use that usual speed. When the conversion enzyme runs fast, a normal codeine tablet can create much more morphine, much sooner.",
    },
    {
      type: 'reveal',
      heading: "It was not simple sensitivity",
      body: "Brigid's pharmacogenetic test showed a CYP2D6 gene duplication: extra working copies of the enzyme that turns codeine into morphine. That means a standard tablet can be converted faster and more completely than expected. She had never taken opioids before, so she had no tolerance. She felt as if she had taken too much because, in morphine effect, the dose behaved like too much. Her sister's similar post-surgery reaction now looks like the same inherited risk showing up twice.",
    },
    {
      type: 'reveal',
      heading: "The result matters for the next prescription too",
      body: "Codeine is not the only medication that uses CYP2D6. Tramadol follows the same route and carries the same danger for Brigid. Hydrocodone is similar. The enzyme also affects some antidepressants, blood pressure medicines, and ondansetron, an anti-nausea drug she sees often at work. For many drugs, ultra-rapid metabolism can mean the medicine clears too quickly. For codeine and tramadol, it can mean a normal prescription becomes unsafe. This finding needs to be visible in her medical record, not just remembered.",
    },
    {
      type: 'action',
      heading: "Five safety steps",
      items: [
        "Add CYP2D6 ultra-rapid metabolizer status to her medical record and carry written confirmation. Any clinician prescribing pain relief needs to see it.",
        "Before future surgery or anesthesia, mention this result during pre-op assessment. Alternatives to codeine, such as acetaminophen, NSAIDs, or non-CYP2D6-dependent options, should be discussed with the clinical team.",
        "Treat tramadol like codeine. It is also a prodrug that uses the same enzyme, so the same overdose risk applies.",
        "Ask new prescribers to check whether a medication is affected by CYP2D6. Many are not dangerous for her, but dose or effectiveness may change.",
        "Encourage her sister to get tested. The family pattern makes a shared CYP2D6 duplication plausible, and confirming it could prevent another unsafe prescription.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: "How CYP2D6 status changes what a 30mg codeine dose means",
      caption:
        "This is not a risk score. It shows why the same tablet created a different experience. A normal metabolizer gets a modest morphine equivalent. Brigid may get two to three times that amount, faster, with no opioid tolerance.",
      baselineLabel: "Normal CYP2D6 metabolizer — 30mg codeine → ~3mg morphine equivalent",
      baseline: 30,
      levers: [
        {
          label: 'CYP2D6 ultra-rapid metabolizer (gene duplication)',
          delta: 40,
          description:
            "Extra functional copies of CYP2D6 accelerate conversion of codeine to morphine. Where a normal metabolizer converts ~10% of the dose, Brigid converts it faster and more completely — producing 6–10mg morphine equivalent from a 30mg tablet instead of the expected 3mg.",
        },
        {
          label: 'Fasting state at time of dose (faster absorption)',
          delta: 8,
          description:
            "Taking codeine on an empty stomach accelerates absorption and increases peak plasma concentration. With ultra-rapid conversion already driving elevated morphine levels, faster absorption compounds the peak exposure.",
        },
        {
          label: 'Opioid-naive patient (no tolerance)',
          delta: 12,
          description:
            "Brigid had no prior opioid exposure. Opioid tolerance reduces the effect of a given morphine dose. Without it, the full pharmacological effect of her elevated morphine conversion was unopposed.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'opioid-brigid',
    label: 'Explore your CYP2D6 and drug metabolism genetics in Coach',
  },
}
